import { useState, useMemo, useEffect, useCallback, useRef } from "react";

import MonitorService from "services/monitor";

import { groupBy } from "helpers/arr";

import { mapMessagesToDeviceInfos } from "../helper";

const incrementPosHits = (_stack, _scale, _x, _y) => {
  const Y = Math.round(_x * _scale) / _scale;
  const X = Math.round(_y * _scale) / _scale;

  const agKey = `${X}, ${Y}`;

  if (!_stack[agKey]) {
    _stack[agKey] = {
      key: agKey,
      x: X,
      y: Y,
      dcX: _x,
      dcY: _y,
      hits: 0,
    };
  }

  _stack[agKey].hits += 1;
};

const getMaxHits = (_stack) => {
  let max = 0;

  Object.values(_stack).forEach(record => {
    max = Math.max(max, record.hits);
  });

  return max;
}

const useCartsHeatmap = ({ selectedCart, currentTrackingMessages, heatmapDecimal }) => {
  const monitorService = useMemo(() => new MonitorService(), []);
  const { id: selectedCartId } = selectedCart || {};

  const [bleLogs, setBleLogs] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { current: historyLoadedForCartIds } = useRef(new Set());

  const loadBleLogs = useCallback(async (cartId) => {
    const now = Math.floor(Date.now() / 1000);
    const requests = [];

    let fromTs = now - 3600; // -1h

    while (fromTs < now) {
      const toTs = fromTs + 600; // 10min

      requests.push(
        monitorService.getBleLogs({
          from_ts: fromTs,
          to_ts: toTs,
          tag_id: cartId,
        })
      );

      fromTs += 600;
    }

    return await Promise.allSettled(requests).then((results) => {
      results = results.filter(({ status }) => status === 'fulfilled').map(r => r.value);
      return results.reduce((allLogs, data) => {
        return allLogs.concat(mapMessagesToDeviceInfos(data.logs));
      }, []);
    });
  }, [monitorService]);

  // register new logs for all carts
  useEffect(() => {
    const cartTrackingLogs = currentTrackingMessages.filter(message => {
      return message.type === 'cartTag';
    });

    if (cartTrackingLogs.length > 0) {
      setBleLogs(bleLogs => {
        const logsByCartId = groupBy(cartTrackingLogs, 'id');

        Object.keys(logsByCartId).forEach(cartId => {
          bleLogs[cartId] = (bleLogs[cartId] || []).concat(logsByCartId[cartId]);
        });

        return bleLogs;
      });
    }
  }, [currentTrackingMessages]);

  // get previous logs for selected cart
  // NOTE: probably we should get logs for all cart and save them
  useEffect(() => {
    if (selectedCartId) {
      if (!historyLoadedForCartIds.has(selectedCartId)) {
        setIsLoading(true);
        historyLoadedForCartIds.add(selectedCartId);

        loadBleLogs(selectedCartId).then(logs => {
          setBleLogs(bleLogs => {
            bleLogs[selectedCartId] = logs.concat(bleLogs[selectedCartId] || []);
            return bleLogs;
          });
          setIsLoading(false);
        });
      }
    }
  }, [monitorService, loadBleLogs, historyLoadedForCartIds, selectedCartId]);


  return useMemo(() => {
    let total = 0;
    const hits = {};

    if (selectedCartId) {
      const logs = bleLogs[selectedCartId] || [];

      logs.forEach(cartLog => {
        incrementPosHits(
          hits,
          heatmapDecimal,
          cartLog.x,
          cartLog.y,
        );
        total += 1;
      });
    }

    return {
      loading: isLoading,
      hits,
      total,
      max: getMaxHits(hits),
    };

  }, [selectedCartId, bleLogs, isLoading, heatmapDecimal]);
};

export default useCartsHeatmap;